/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { useEffect, useState } from "react"
import { VideoTrack } from "twilio-video"

/*
 * Sizing for the live exam's video boxes. Each box takes the shape of the picture it shows, so that the picture fills
 * it with no bars, and both people in the exam see the patient's picture at the same proportions. That also makes a
 * position given as a fraction of the box the same point of the picture on every screen, which is how the
 * healthcare provider's crosshair reaches the patient.
 */

type Size = { width: number; height: number }

// The tallest a live exam video is shown. A taller picture shows nothing more, and on a tall window, such as a browser
// filling one side of a large monitor, the patient's picture of themselves would be oversized.
const maxVideoHeightPx = 800

/**
 * The largest size with the given aspect ratio (width / height) that fits in `area`, in whole pixels.
 */
const fitAspectRatio = (area: Size, aspectRatio: number): Size => {
  if (area.width <= 0 || area.height <= 0 || aspectRatio <= 0) {
    return { width: 0, height: 0 }
  }
  const height = Math.min(area.height, area.width / aspectRatio)
  return { width: Math.floor(height * aspectRatio), height: Math.floor(height) }
}

/**
 * The size of a live exam video: the largest size with the given aspect ratio that fits in `area`, but no taller than
 * `maxVideoHeightPx`.
 */
const fitVideo = (area: Size, aspectRatio: number): Size =>
  fitAspectRatio({ width: area.width, height: Math.min(area.height, maxVideoHeightPx) }, aspectRatio)

/**
 * The aspect ratio (width / height) of a track's picture, kept current as the picture changes size. Until the track
 * has a picture, or when there is no track, this is `fallback`.
 */
const useVideoAspectRatio = (track: VideoTrack | null | undefined, fallback: number): number => {
  const [aspectRatio, setAspectRatio] = useState(fallback)

  useEffect(() => {
    const update = () => {
      const width = track?.dimensions.width
      const height = track?.dimensions.height
      setAspectRatio(width && height ? width / height : fallback)
    }
    update()
    track?.on("dimensionsChanged", update)
    return () => {
      track?.off("dimensionsChanged", update)
    }
  }, [track, fallback])

  return aspectRatio
}

/**
 * The size of an element, kept current as the element resizes: its content box, or with `box` "border-box" the whole
 * element including padding and border. Pass the returned ref callback as the element's `ref`. The size is zero until
 * the element is first measured.
 */
const useElementSize = (
  box: "content-box" | "border-box" = "content-box"
): [(element: HTMLElement | null) => void, Size] => {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  useEffect(() => {
    if (!element || typeof ResizeObserver === "undefined") {
      return
    }
    const observer = new ResizeObserver(([entry]) => {
      const borderBox = entry.borderBoxSize?.[0]
      const measured =
        box === "border-box" && borderBox
          ? { width: borderBox.inlineSize, height: borderBox.blockSize }
          : entry.contentRect
      const width = Math.floor(measured.width)
      const height = Math.floor(measured.height)
      setSize((current) => (current.width === width && current.height === height ? current : { width, height }))
    })
    observer.observe(element, { box })
    return () => observer.disconnect()
  }, [element, box])

  return [setElement, size]
}

export { fitAspectRatio, fitVideo, maxVideoHeightPx, useElementSize, useVideoAspectRatio }
export type { Size }
